/**
 * Payslip release: preview, publication, reissue, download and the per-entity publication policy
 * (ADR 0014 output track, ADR 0015 payment service).
 *
 *     released run ─▶ payroll_result_outputs (immutable, one per employee result of the final version)
 *                        └── publication v1 (published) ──reissue──▶ v1 superseded, v2 published ─▶ ...
 *
 * CONTRACT FIRST (CLAUDE.md invariant 8). This file declares routes and schemas; nothing implements
 * them yet.
 *
 * A PUBLICATION IS A VERSIONED RECORD AGAINST AN IMMUTABLE RESULT. Each publication names the
 * `payroll_result_outputs` row (`outputId`), the finalized result version it was rendered from, and the
 * sha256 checksum of the stored document. A correction or reissue creates a NEW publication with
 * `documentVersion + 1` and a mandatory reason; the earlier one is marked `superseded` and kept, never
 * deleted, so what an employee was shown on a given day is always answerable.
 *
 * PAYMENT STATUS AND PUBLICATION STATUS ARE SEPARATE FIELDS. A payslip records what was EARNED; a
 * transfer records what was CREDITED. Whether one waits for the other is the legal entity's explicit
 * `PayslipPublicationPolicy` (`on_release | after_payment_confirmed | manual`), never an implicit
 * withholding because a transfer failed.
 *
 * RELATIONSHIP TO `payrollLifecycle.publishPayslips`. That route stays for compatibility: it only flips
 * `payslip_status`. `publishRelease` here is the full act — it renders each payslip from the final
 * result, stores the document privately, records a publication row, flips `payslip_status`, and emits
 * the `payroll.payslip_published` notification through `@hrms/notify` AFTER COMMIT (outbox). The server
 * applies the entity's policy; the caller cannot opt out of it.
 *
 * DOWNLOADS are short-lived private links issued after the server has authorised ownership (own
 * routes) or scope (HR routes). No payslip document is ever protected with a PAN or any other
 * identifier as a password, and no password is modelled here. Both download routes are hidden from the
 * copilot: the link is a bearer credential to a salary document and has no business in a prompt.
 *
 * EVERY WRITE TAKES `Idempotency-Key` (invariant 10); no read does. Each route declares exactly ONE
 * permission, so the copilot's tool list (`payrollPayslips__<key>`) is filtered by it precisely.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { CURRENCIES } from '../enums.js';
import { PAYMENT_STATUSES, PAYSLIP_STATUSES } from '../payroll-lifecycle.js';
import { PAYMENT_OBLIGATION_STATES, PAYSLIP_PUBLICATION_POLICIES } from '../payroll-payments.js';
import {
  decimalStringSchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  sha256HexSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';
import { payslipPreviewSchema } from './payroll-runs.contract.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });
const outputParams = z.object({ outputId: uuidSchema });
const currencySchema = z.enum(CURRENCIES);
const count = z.number().int().nonnegative();

export const payslipPublicationPolicySchema = z.enum(PAYSLIP_PUBLICATION_POLICIES);
export const payslipPublicationStateSchema = z.enum(['published', 'superseded']);
export const releasePayslipStatusSchema = z.enum(PAYSLIP_STATUSES);
export const releasePaymentStatusSchema = z.enum(PAYMENT_STATUSES);
export const releaseObligationStateSchema = z.enum(PAYMENT_OBLIGATION_STATES);

/** Why a payslip cannot be published under the entity's policy right now. */
export const PAYSLIP_INELIGIBLE_REASONS = [
  /** `after_payment_confirmed`, and the output's payment has not reached `paid`. */
  'payment_not_confirmed',
  /** `manual`: publication is a deliberate per-release act, never implied by the release. */
  'manual_policy',
  /** Already has a current publication (use reissue to correct it). */
  'already_published',
] as const;
export const payslipIneligibleReasonSchema = z.enum(PAYSLIP_INELIGIBLE_REASONS);

// ---------------------------------------------------------------------------
// Release overview and rows
// ---------------------------------------------------------------------------

export const releaseQuerySchema = z.object({ runId: uuidSchema });

export const payslipReleaseOverviewSchema = z.object({
  runId: uuidSchema,
  periodLabel: z.string(),
  legalEntityId: uuidSchema,
  policy: payslipPublicationPolicySchema,
  /**
   * Outputs by publication state. `superseded` counts outputs whose current publication is a reissue
   * (they are also published); it is informational, not a third bucket of employees.
   */
  counts: z.object({
    not_published: count,
    published: count,
    superseded: count,
  }),
  /** Unpublished outputs the policy allows publishing now. */
  readyToPublish: count,
  /** Unpublished outputs the policy holds back, and the single reason the policy gives. */
  blockedByPolicy: z.object({
    count,
    reason: payslipIneligibleReasonSchema.nullable(),
  }),
  /**
   * The channels a publication notification will actually go out on. ONLY integrations that exist and
   * are configured for the organisation are listed (always `in_app`; `email`, `push`, `sms`, `whatsapp`
   * only when set up). The UI shows email / mobile mirroring only when its channel is listed here.
   */
  notificationChannels: z.array(z.string()),
});
export type PayslipReleaseOverview = z.infer<typeof payslipReleaseOverviewSchema>;

export const releaseRowsQuerySchema = z.object({
  runId: uuidSchema,
  payslipStatus: releasePayslipStatusSchema.optional(),
  paymentStatus: releasePaymentStatusSchema.optional(),
  /** Name or employee code, case-insensitive contains. */
  q: z.string().trim().min(1).max(100).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().min(5).max(100).default(25),
});

export const currentPublicationSchema = z.object({
  id: uuidSchema,
  documentVersion: z.number().int().positive(),
  publishedAt: instantSchema,
  publishedByName: z.string().nullable(),
});

export const payslipReleaseRowSchema = z.object({
  outputId: uuidSchema,
  employmentId: uuidSchema,
  employeeCode: z.string(),
  personName: z.string(),
  netPayable: decimalStringSchema,
  currency: currencySchema,
  payslipStatus: releasePayslipStatusSchema,
  /** ADR 0014's monotonic output track. A retryable failure lives on the obligation, not here. */
  paymentStatus: releasePaymentStatusSchema,
  /** The payment service's obligation for this output, when one exists (ADR 0015). */
  obligationState: releaseObligationStateSchema.nullable(),
  currentPublication: currentPublicationSchema.nullable(),
  eligibleUnderPolicy: z.boolean(),
  ineligibleReason: payslipIneligibleReasonSchema.nullable(),
});
export type PayslipReleaseRow = z.infer<typeof payslipReleaseRowSchema>;

export const payslipReleaseRowsSchema = z.object({
  runId: uuidSchema,
  items: z.array(payslipReleaseRowSchema),
  total: count,
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type PayslipReleaseRows = z.infer<typeof payslipReleaseRowsSchema>;

// ---------------------------------------------------------------------------
// Preview (HR view) — rendered from the FINAL result, never a draft
// ---------------------------------------------------------------------------

/**
 * The payslip as it is (or will be) published, rendered from the immutable finalized result. The HR
 * view keeps employer contributions; the employee's own view (`myPayslipSchema`) omits them.
 */
export const finalPayslipPreviewSchema = payslipPreviewSchema
  .omit({ isDraft: true, draftReason: true })
  .extend({
    outputId: uuidSchema,
    runId: uuidSchema,
    /** The finalized calculation version the result belongs to. */
    resultVersionId: uuidSchema,
    finalizedAt: instantSchema,
    isFinal: z.literal(true),
    payslipStatus: releasePayslipStatusSchema,
    paymentStatus: releasePaymentStatusSchema,
    currentPublication: currentPublicationSchema.nullable(),
  });
export type FinalPayslipPreview = z.infer<typeof finalPayslipPreviewSchema>;

// ---------------------------------------------------------------------------
// Publications
// ---------------------------------------------------------------------------

export const payslipPublicationSchema = z.object({
  id: uuidSchema,
  documentVersion: z.number().int().positive(),
  state: payslipPublicationStateSchema,
  resultVersionId: uuidSchema,
  /** sha256 of the stored document bytes: what the employee downloads is provably what was published. */
  checksum: sha256HexSchema,
  publishedAt: instantSchema,
  publishedByName: z.string().nullable(),
  supersededAt: instantSchema.nullable(),
  supersededByPublicationId: uuidSchema.nullable(),
  /** Null on a first publication; required on every reissue. */
  reason: z.string().nullable(),
  /** When the notification outbox event was relayed; null until then (or if none was sent). */
  notifiedAt: instantSchema.nullable(),
});
export type PayslipPublication = z.infer<typeof payslipPublicationSchema>;

export const payslipPublicationListSchema = z.object({
  outputId: uuidSchema,
  items: z.array(payslipPublicationSchema),
});
export type PayslipPublicationList = z.infer<typeof payslipPublicationListSchema>;

/** The employee's view of their own payslip's versions: no employer cost, no publisher identity. */
export const myPayslipPublicationSchema = payslipPublicationSchema.omit({
  publishedByName: true,
  notifiedAt: true,
});
export const myPayslipPublicationListSchema = z.object({
  outputId: uuidSchema,
  items: z.array(myPayslipPublicationSchema),
});
export type MyPayslipPublicationList = z.infer<typeof myPayslipPublicationListSchema>;

// ---------------------------------------------------------------------------
// Download
// ---------------------------------------------------------------------------

export const payslipDownloadQuerySchema = z.object({
  /** A specific (possibly superseded) publication; omitted means the current one. */
  publicationId: uuidSchema.optional(),
});

/**
 * A short-lived private link to the stored document. The link is issued only after the server has
 * authorised the caller for this output; it carries no password and the document has none.
 */
export const payslipDownloadSchema = z.object({
  url: z.string().url(),
  expiresAt: instantSchema,
  fileName: z.string(),
});
export type PayslipDownload = z.infer<typeof payslipDownloadSchema>;

// ---------------------------------------------------------------------------
// Publish and reissue
// ---------------------------------------------------------------------------

/**
 * Publish payslips of a RELEASED run. `outputIds` omitted publishes every output the policy allows;
 * supplied, exactly those (each must belong to the run). The server ALWAYS applies the legal entity's
 * policy: an output the policy holds back is skipped and counted, never forced. `respectPolicy` exists
 * only so a caller states that expectation explicitly; it cannot be `false`.
 */
export const publishReleaseBodySchema = z.object({
  runId: uuidSchema,
  outputIds: z.array(uuidSchema).min(1).max(5000).optional(),
  respectPolicy: z.literal(true).optional(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type PublishReleaseBody = z.infer<typeof publishReleaseBodySchema>;

/** Why one payslip could not be published this time. A fixed set: no internals reach the client. */
export const PAYSLIP_PUBLISH_FAILURE_REASONS = [
  'prepare_failed',
  'render_failed',
  'store_failed',
] as const;
export const publishFailureSchema = z.object({
  outputId: uuidSchema,
  employeeCode: z.string(),
  personName: z.string(),
  reason: z.enum(PAYSLIP_PUBLISH_FAILURE_REASONS),
});
export type PublishFailure = z.infer<typeof publishFailureSchema>;

export const publishReleaseResultSchema = z.object({
  runId: uuidSchema,
  /** Publications this request created. */
  published: count,
  /** Outputs the policy held back. */
  skippedByPolicy: count,
  /** Outputs that already had a current publication. */
  alreadyPublished: count,
  /** The run's publication status after this request. */
  payslipStatus: releasePayslipStatusSchema,
  /**
   * ADDITIVE (older clients ignore these). Outputs whose document could not be produced this time:
   * they stay unpublished (nothing half-published) and publishing them again is safe.
   */
  failed: count.optional(),
  failures: z.array(publishFailureSchema).max(5000).optional(),
  /** Eligible outputs left for the next request (a request renders a bounded batch). */
  remaining: count.optional(),
});
export type PublishReleaseResult = z.infer<typeof publishReleaseResultSchema>;

/**
 * Reissue a published payslip. `expectedPublicationId` is the CURRENT publication the caller saw; if
 * another reissue landed first, it is a 409 and nothing changes. The reason is recorded on the new
 * publication and in the audit trail.
 */
export const reissuePayslipBodySchema = z.object({
  expectedPublicationId: uuidSchema,
  reason: z.string().trim().min(10).max(1000),
});
export type ReissuePayslipBody = z.infer<typeof reissuePayslipBodySchema>;

// ---------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------

export const payslipPolicyQuerySchema = z.object({ legalEntityId: uuidSchema });

export const payslipPolicySchema = z.object({
  legalEntityId: uuidSchema,
  policy: payslipPublicationPolicySchema,
  /** Null while the entity is on the default and nobody has set it. */
  updatedAt: instantSchema.nullable(),
  updatedByName: z.string().nullable(),
});
export type PayslipPolicy = z.infer<typeof payslipPolicySchema>;

/**
 * Set an entity's policy. `expectedUpdatedAt` is the `updatedAt` the caller loaded (null when it was
 * never set); a policy changed meanwhile is a 409. Omitted, the write is unconditional.
 */
export const setPayslipPolicyBodySchema = z.object({
  legalEntityId: uuidSchema,
  policy: payslipPublicationPolicySchema,
  expectedUpdatedAt: instantSchema.nullable().optional(),
});
export type SetPayslipPolicyBody = z.infer<typeof setPayslipPolicyBodySchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

/** Static segments (`release`, `release/rows`, `release/publish`, `policy`, `mine`) precede parameters. */
export const payrollPayslipsContract = c.router(
  {
    getReleaseOverview: {
      method: 'GET',
      path: '/release',
      query: releaseQuerySchema,
      responses: {
        200: payslipReleaseOverviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** The run is not released. */
        412: errorBodySchema,
      },
      summary: "A released run's payslip publication summary under its entity's policy",
      metadata: routeMeta('payslip:read'),
    },

    listReleaseRows: {
      method: 'GET',
      path: '/release/rows',
      query: releaseRowsQuerySchema,
      responses: {
        200: payslipReleaseRowsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Per-employee payslip publication and payment status of a released run',
      metadata: routeMeta('payslip:read', { salaryPin: 'others' }),
    },

    publishRelease: {
      method: 'POST',
      path: '/release/publish',
      headers: idempotencyHeadersSchema,
      body: publishReleaseBodySchema,
      responses: {
        200: publishReleaseResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** A named output does not belong to the run, or nothing could be published. */
        409: errorBodySchema,
        /** The run is not released. */
        412: errorBodySchema,
      },
      summary: 'Render, store and publish payslips of a released run, then notify employees',
      metadata: routeMeta('payslip:publish'),
    },

    getPolicy: {
      method: 'GET',
      path: '/policy',
      query: payslipPolicyQuerySchema,
      responses: {
        200: payslipPolicySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "A legal entity's payslip publication policy",
      metadata: routeMeta('payslip:read'),
    },

    setPolicy: {
      method: 'PUT',
      path: '/policy',
      headers: idempotencyHeadersSchema,
      body: setPayslipPolicyBodySchema,
      responses: {
        200: payslipPolicySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** The policy changed since the caller loaded it. */
        409: errorBodySchema,
      },
      summary: "Set a legal entity's payslip publication policy",
      metadata: routeMeta('payslip.policy:manage'),
    },

    previewPayslip: {
      method: 'GET',
      path: '/outputs/:outputId/preview',
      pathParams: outputParams,
      responses: {
        200: finalPayslipPreviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Preview a payslip rendered from the final payroll result, with employer cost',
      // Any-of at the gate: `payroll:read` (a wider payroll-admin view) reaches the handler,
      // which still checks the narrower `payslip:read` per record (`assertCanSee`) — a caller
      // with neither gets 403 at the gate; a caller with `payroll:read` but no `payslip:read`
      // anywhere gets a scoped 404 from the handler, never learning the record exists.
      metadata: routeMeta(['payslip:read', 'payroll:read'], { salaryPin: 'subject' }),
    },

    listPublications: {
      method: 'GET',
      path: '/outputs/:outputId/publications',
      pathParams: outputParams,
      responses: {
        200: payslipPublicationListSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Every published version of an employee's payslip, including superseded ones",
      metadata: routeMeta('payslip:read'),
    },

    getPayslipDownload: {
      method: 'GET',
      path: '/outputs/:outputId/download',
      pathParams: outputParams,
      query: payslipDownloadQuerySchema,
      responses: {
        200: payslipDownloadSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** No such output in scope, or it has no publication. */
        404: errorBodySchema,
      },
      summary: 'A short-lived private link to a published payslip document',
      metadata: routeMeta('payslip:read', { ai: 'hidden', salaryPin: 'subject' }),
    },

    reissuePayslip: {
      method: 'POST',
      path: '/outputs/:outputId/reissue',
      pathParams: outputParams,
      headers: idempotencyHeadersSchema,
      body: reissuePayslipBodySchema,
      responses: {
        200: payslipPublicationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** `expectedPublicationId` is not the current publication. */
        409: errorBodySchema,
        /** The output has never been published: publish it first. */
        412: errorBodySchema,
      },
      summary: 'Reissue a published payslip as a new version, superseding the current one',
      metadata: routeMeta('payslip:publish'),
    },

    getMyPayslipDownload: {
      method: 'GET',
      path: '/mine/:outputId/download',
      pathParams: outputParams,
      responses: {
        200: payslipDownloadSchema,
        401: errorBodySchema,
        /**
         * Not mine, or not published: 404, never 403, so the response does not confirm that someone
         * else's payslip exists.
         */
        404: errorBodySchema,
      },
      summary: 'A short-lived private link to my current published payslip',
      metadata: routeMeta('payslip.own:read', { ai: 'hidden' }),
    },

    listMyPublications: {
      method: 'GET',
      path: '/mine/:outputId/publications',
      pathParams: outputParams,
      responses: {
        200: myPayslipPublicationListSchema,
        401: errorBodySchema,
        /** Not mine, or not published (never 403). */
        404: errorBodySchema,
      },
      summary: 'The published versions of one of my payslips',
      metadata: routeMeta('payslip.own:read'),
    },
  },
  { pathPrefix: '/payroll/payslips' },
);
